package org.example.project.screens_driver

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.decodeFromJsonElement
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.RegisterResourceSyncHandler
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.service.YardApi
import org.example.project.service.YardFlowApi
import org.example.project.service.YardSettingsService
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.example.project.yard.suggestWeTorOnArrival
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun DRIVERYardLobbyScreen() {
    val t: (String) -> String = { LocalizationManager.get(it) }
    val auftragId = DriverFlowState.selectedWeAuftragId.value.trim()
    val lieferscheinnummer = DriverFlowState.selectedYardLieferscheinnummer.value.trim()
    val providerFirmenId = DriverFlowState.selectedWeProviderFirmenId.value.trim()
    val auftragArt = DriverFlowState.selectedAuftragArt.value
    val cachedAuftrag = DriverFlowState.selectedYardAuftrag.value
    var loading by androidx.compose.runtime.remember(auftragId, lieferscheinnummer) {
        mutableStateOf(auftragId.isNotBlank() && cachedAuftrag?.id != auftragId)
    }
    var eintrag by androidx.compose.runtime.remember(auftragId, lieferscheinnummer) {
        mutableStateOf(cachedAuftrag?.takeIf { it.id == auftragId })
    }
    var initialCacheUsed by androidx.compose.runtime.remember(auftragId, lieferscheinnummer) { mutableStateOf(false) }
    var parkplaetzeImYardBenutzen by androidx.compose.runtime.remember { mutableStateOf(true) }
    var ankunftSpeichern by androidx.compose.runtime.remember(auftragId, lieferscheinnummer) { mutableStateOf(false) }
    var nextWaUebergang by androidx.compose.runtime.remember(auftragId, lieferscheinnummer) { mutableStateOf<All?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        parkplaetzeImYardBenutzen = YardSettingsService.parkplaetzeImYardBenutzen()
    }

    suspend fun loadCurrentAuftrag(): All? =
        runCatching {
            DriverAuftraegeApi.loadCurrent(
                providerFirmenId = providerFirmenId,
                artCode = auftragArt,
                auftragId = auftragId,
                lieferscheinnummer = lieferscheinnummer
            )
        }.getOrNull()

    fun yardVerlassen() {
        DriverFlowState.selectedWeAuftragId.value = ""
        DriverFlowState.selectedYardLieferscheinnummer.value = ""
        DriverFlowState.selectedWeProviderFirmenId.value = ""
        DriverFlowState.selectedYardAuftrag.value = null
        DriverFlowState.selectedScreenKey.value = "DASHBOARD"
    }

    fun setSelectedYardAuftrag(next: All, nextProviderFirmenId: String = providerFirmenId, nextAuftragArt: String = auftragArt) {
        DriverFlowState.selectedWeAuftragId.value = next.id
        DriverFlowState.selectedYardLieferscheinnummer.value = next.lieferscheinnummer
        DriverFlowState.selectedWeProviderFirmenId.value = nextProviderFirmenId
        DriverFlowState.selectedAuftragArt.value = nextAuftragArt
        DriverFlowState.selectedYardAuftrag.value = next
        eintrag = next
    }

    RegisterResourceSyncHandler("DriverYardLobbyScreen") { event ->
        if (event.group != "yard") return@RegisterResourceSyncHandler
        val expectedType = if (auftragArt == SLOT_BEREICH_WA) "wa-lkw" else "we-lkw"
        if (event.resourceType != expectedType) return@RegisterResourceSyncHandler
        val incoming = event.data?.let { runCatching { Json.decodeFromJsonElement<All>(it) }.getOrNull() }
            ?: return@RegisterResourceSyncHandler
        val idMatches = incoming.id == auftragId || event.resourceId == auftragId
        val lsMatches = auftragArt != SLOT_BEREICH_WA || lieferscheinnummer.isBlank() || incoming.lieferscheinnummer == lieferscheinnummer
        if (idMatches && lsMatches) {
            setSelectedYardAuftrag(incoming)
            loading = false
        }
    }

    suspend fun nextOpenWaAuftragInGroup(current: All): All? {
        val key = current.driverLobbyWaSammelKey()
        return YardApi.loadWaLkwListe(providerFirmenId)
            .filter { it.driverLobbyWaSammelKey() == key }
            .filter { it.zustand < ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT }
            .sortedWith(compareBy<All> { it.ablaufPrio.takeIf { prio -> prio > 0 } ?: Int.MAX_VALUE }.thenBy { it.lieferscheinnummer }.thenBy { it.id })
            .firstOrNull()
    }

    suspend fun targetWeAuftragForEndToEnd(current: All): Pair<String, All>? {
        if (current.endToEndFlow != true || current.flowTyp != "wa_to_we") return null
        val targetFirmenId = current.flowTargetFirmenId.trim()
        if (targetFirmenId.isBlank()) return null
        val sourceLs = current.flowTargetWeLieferscheinnummer.ifBlank { current.lieferscheinnummer }.trim()
        val targetRows = YardApi.loadWeLkwListe(targetFirmenId)
        val target = targetRows.firstOrNull { row ->
            current.flowId.isNotBlank() && row.flowId == current.flowId
        } ?: targetRows.firstOrNull { row ->
            sourceLs.isNotBlank() &&
                row.lieferscheinnummer == sourceLs &&
                row.lkwkennzeichen.trim().equals(current.lkwkennzeichen.trim(), ignoreCase = true) &&
                row.aufliegerkennzeichen.trim().equals(current.aufliegerkennzeichen.trim(), ignoreCase = true) &&
                row.fahrername.trim().equals(current.fahrername.trim(), ignoreCase = true)
        }
        return target?.let { targetFirmenId to it }
    }

    LaunchedEffect(auftragId, lieferscheinnummer, providerFirmenId, auftragArt, cachedAuftrag) {
        if (auftragId.isBlank()) {
            loading = false
            eintrag = null
            return@LaunchedEffect
        }

        if (
            !initialCacheUsed &&
            cachedAuftrag?.id == auftragId &&
            (auftragArt != SLOT_BEREICH_WA || lieferscheinnummer.isBlank() || cachedAuftrag.lieferscheinnummer == lieferscheinnummer)
        ) {
            loading = false
            eintrag = cachedAuftrag
            initialCacheUsed = true
            return@LaunchedEffect
        }

        loading = true
        eintrag = loadCurrentAuftrag()
        loading = false
    }

    LaunchedEffect(auftragId, lieferscheinnummer, providerFirmenId, auftragArt) {
        if (auftragId.isBlank()) return@LaunchedEffect
        while (true) {
            delay(2000)
            if ((eintrag?.zustand ?: ZustandManager.RESERVIERT) < ZustandManager.RESERVIERT) continue
            val latest = loadCurrentAuftrag()
            if (latest != null && latest != eintrag) {
                eintrag = latest
            }
        }
    }

    when {
        loading -> Div { Text(t("driver_loading_yard_order")) }
        nextWaUebergang != null -> DriverWaNextStandortTransition(
            auftrag = nextWaUebergang ?: return,
            onFortfuehren = {
                val next = nextWaUebergang ?: return@DriverWaNextStandortTransition
                nextWaUebergang = null
                setSelectedYardAuftrag(next)
            }
        )
        eintrag == null -> Div { Text(t("driver_no_linked_yard_order")) }
        else -> {
            val lobbyEintrag = eintrag ?: return
            FahrerLobbyScreen(
                eintrag = lobbyEintrag,
                isWeAuftrag = auftragArt != SLOT_BEREICH_WA,
                ankunftBestaetigungErforderlich = lobbyEintrag.zustand < ZustandManager.RESERVIERT,
                onAnkunftBestaetigt = {
                    val aktuellerEintrag = eintrag
                    if (aktuellerEintrag != null && !ankunftSpeichern && aktuellerEintrag.zustand < ZustandManager.RESERVIERT) {
                        ankunftSpeichern = true
                        scope.launch {
                            runCatching {
                                val zuSpeichern = if (auftragArt == SLOT_BEREICH_WA) {
                                    aktuellerEintrag.copy(waStandortAnkunft = true)
                                } else {
                                    aktuellerEintrag
                                }
                                val angekommen = YardFlowApi.fahrerAnkunftBestaetigen(
                                    auftrag = zuSpeichern,
                                    artCode = auftragArt,
                                    firmenId = providerFirmenId
                                )
                                if (auftragArt != SLOT_BEREICH_WA && parkplaetzeImYardBenutzen) {
                                    suggestWeTorOnArrival(angekommen, providerFirmenId).auftrag
                                } else {
                                    angekommen
                                }
                            }.onSuccess { aktualisiert ->
                                eintrag = aktualisiert
                                DriverFlowState.selectedYardAuftrag.value = aktualisiert
                            }
                            ankunftSpeichern = false
                        }
                    }
                },
                onWaDokumenteBestaetigt = { abgeschlossen ->
                    if (auftragArt != SLOT_BEREICH_WA) {
                        yardVerlassen()
                        return@FahrerLobbyScreen
                    }
                    scope.launch {
                        val next = nextOpenWaAuftragInGroup(abgeschlossen)
                        if (next != null) {
                            nextWaUebergang = next
                        } else {
                            val targetWe = targetWeAuftragForEndToEnd(abgeschlossen)
                            if (targetWe != null) {
                                setSelectedYardAuftrag(targetWe.second, targetWe.first, SLOT_BEREICH_WE)
                            } else {
                                yardVerlassen()
                            }
                        }
                    }
                },
                onYardVerlassen = { yardVerlassen() },
                parkplaetzeImYardBenutzen = parkplaetzeImYardBenutzen
            )
        }
    }
}

@Composable
private fun DriverWaNextStandortTransition(
    auftrag: All,
    onFortfuehren: () -> Unit
) {
    Div({
        style {
            width(100.percent)
            height(100.percent)
            minHeight(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.FlexStart)
            gap(18.px)
            padding(26.px, 18.px, 18.px, 18.px)
            boxSizing("border-box")
            backgroundColor(Color.transparent)
            color(DashboardTheme.TEXT_LIGHT)
            textAlign("left")
        }
    }) {
        Span({
            style {
                width(100.percent)
                maxWidth(420.px)
                fontSize(15.px)
                fontWeight("800")
                lineHeight("1.5")
            }
        }) {
            Text(LocalizationManager.get("driver_next_loading_place").replace("{place}", auftrag.lfdNr.takeIf { it > 0 }?.toString() ?: auftrag.id))
            Div { Text(LocalizationManager.get("driver_drive_to_next_loading_place")) }
            Div { Text(driverWaNextStandortAddress(auftrag)) }
        }
        Div({ style { width(100.percent); maxWidth(320.px) } }) {
            LumaticaButton(
                label = LocalizationManager.get("driver_continue_order"),
                onClick = onFortfuehren,
                variant = LumaticaButtonVariant.Action,
                heightPx = 42
            )
        }
    }
}

private fun driverWaNextStandortAddress(auftrag: All): String =
    listOf(
        auftrag.standort,
        listOf(auftrag.strasse, auftrag.hausnummer).filter { it.isNotBlank() }.joinToString(" "),
        listOf(auftrag.plz, auftrag.ort).filter { it.isNotBlank() }.joinToString(" ")
    ).filter { it.isNotBlank() }.joinToString(", ").ifBlank { auftrag.platz.ifBlank { auftrag.platzcode } }

private fun All.driverLobbyWaSammelKey(): String {
    val stored = waSammelPin
        .ifBlank { waSammelId }
        .ifBlank { pin }
        .trim()
    if (stored.isNotBlank()) return "SAMMEL:$stored"
    val physicalLkwKey = listOf(firmenId, lieferscheinnummer, lkwkennzeichen, aufliegerkennzeichen, fahrername)
        .map { it.trim() }
        .joinToString("|")
    return if (lieferscheinnummer.isNotBlank() && (lkwkennzeichen.isNotBlank() || fahrername.isNotBlank())) {
        "WA_LKW:$physicalLkwKey"
    } else {
        "SINGLE:${firmenId}:${lieferscheinnummer}:${id.ifBlank { lfdNr.toString() }}"
    }
}
